import React, { useEffect, useState, useRef } from "react";
import classes from './FixedAsset.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2, X, FileText, Upload, Calendar, DollarSign, Hash, File, Package } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from 'sweetalert2';

const FixedAsset = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState({
        total_assets: 0,
        total_value: 0,
        total_depreciation: 0,
        net_book_value: 0
    });

    const [loading, setLoading] = useState(true);
    const [modalLoading, setModalLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [fixedAssets, setFixedAssets] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewEditModal, setShowViewEditModal] = useState(false);
    const [modalMode, setModalMode] = useState("view");
    const [selectedAsset, setSelectedAsset] = useState(null);

    // Create Modal state
    const [assetCategories, setAssetCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [depreciationMethods, setDepreciationMethods] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const [createFormData, setCreateFormData] = useState({
        asset_name: "",
        asset_code: "",
        description: "",
        serial_number: "",
        category_id: "",
        purchase_date: "",
        supplier_id: "",
        invoice_number: "",
        purchase_cost: "",
        additional_cost: "0",
        capitalized_cost: "",
        residual_value: "0",
        useful_life_years: "",
        depreciation_method: "",
        depreciation_rate: "",
        accumulated_depreciation: "0",
        net_book_value: "",
        asset_document: [],
        company_id: ""
    });

    const [createLoading, setCreateLoading] = useState({
        categories: false,
        suppliers: false,
        methods: false
    });
    const [submitting, setSubmitting] = useState(false);

    // For searchable selects in create modal
    const [searchTerms, setSearchTerms] = useState({
        category: "",
        supplier: "",
        depreciation_method: ""
    });
    const [showDropdowns, setShowDropdowns] = useState({
        category: false,
        supplier: false,
        depreciation_method: false
    });
    const [dropdownTrigger, setDropdownTrigger] = useState({
        category: false,
        supplier: false,
        depreciation_method: false
    });

    // Create refs for each dropdown
    const categoryRef = useRef(null);
    const supplierRef = useRef(null);
    const depreciationMethodRef = useRef(null);
    const fileInputRef = useRef(null);

    // Modal form data for view/edit
    const [modalData, setModalData] = useState({
        id: "",
        asset_name: "",
        asset_code: "",
        description: "",
        serial_number: "",
        category_id: "",
        category_name: "",
        purchase_date: "",
        supplier_id: "",
        supplier_name: "",
        invoice_number: "",
        purchase_cost: "",
        additional_cost: "0",
        capitalized_cost: "",
        residual_value: "0",
        useful_life_years: "",
        depreciation_method_id: "",
        depreciation_method_name: "",
        depreciation_rate: "",
        accumulated_depreciation: "0",
        net_book_value: "",
        documents: []
    });

    // Helper function to get company ID from any source
    const getCompanyIdFromStorage = () => {
        const directId = localStorage.getItem("company_id");
        if (directId) return directId;

        const companyInfo = localStorage.getItem("companyInfo");
        if (companyInfo) {
            try {
                const parsed = JSON.parse(companyInfo);
                const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                if (id) return String(id);
            } catch (e) {
                console.error("Error parsing companyInfo:", e);
            }
        }

        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const parsed = JSON.parse(userData);
                if (parsed.company_id) return String(parsed.company_id);
                if (parsed.company) {
                    const id = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                    if (id) return String(id);
                }
                if (parsed.companies?.length > 0) {
                    const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1 || c.is_main === true);
                    if (mainCompany) {
                        const id = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                        if (id) return String(id);
                    }
                    const firstCompany = parsed.companies[0];
                    const id = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                    if (id) return String(id);
                }
            } catch (e) {
                console.error("Error parsing user data:", e);
            }
        }
        return null;
    };

    // Function to get valid company ID
    const getValidCompanyId = () => {
        if (companyId) return companyId;
        const storageId = getCompanyIdFromStorage();
        if (storageId) {
            setCompanyId(storageId);
            setCreateFormData(prev => ({
                ...prev,
                company_id: storageId
            }));
            return storageId;
        }
        return null;
    };

    // Initialize company ID on mount
    useEffect(() => {
        const id = getCompanyIdFromStorage();
        if (id) {
            console.log("Found company ID:", id);
            setCompanyId(id);
            setCreateFormData(prev => ({
                ...prev,
                company_id: id
            }));
        } else {
            console.warn("No company ID found");
            Swal.fire({
                icon: 'warning',
                title: 'Company ID Missing',
                text: 'Please log in again or select a company.',
                confirmButtonText: 'Go to Login'
            }).then(() => {
                navigate('/login');
            });
        }
    }, [navigate]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (categoryRef.current && !categoryRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, category: false }));
                setDropdownTrigger(prev => ({ ...prev, category: false }));
            }
            if (supplierRef.current && !supplierRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, supplier: false }));
                setDropdownTrigger(prev => ({ ...prev, supplier: false }));
            }
            if (depreciationMethodRef.current && !depreciationMethodRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, depreciation_method: false }));
                setDropdownTrigger(prev => ({ ...prev, depreciation_method: false }));
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    // Fetch data for edit modal when opened
    useEffect(() => {
        if (showViewEditModal && modalMode === "edit") {
            const validCompanyId = getValidCompanyId();
            if (validCompanyId) {
                fetchAssetCategories();
                fetchSuppliers();
                fetchDepreciationMethods();
            }
        }
    }, [showViewEditModal, modalMode]);
    // Fetch data for create modal when opened
    useEffect(() => {
        if (showCreateModal) {
            const validCompanyId = getValidCompanyId();
            if (validCompanyId) {
                fetchAssetCategories();
                fetchSuppliers();
                fetchDepreciationMethods();
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Company ID Missing',
                    text: 'Please log in again.',
                    confirmButtonText: 'Go to Login'
                }).then(() => {
                    setShowCreateModal(false);
                    navigate('/login');
                });
            }
        }
    }, [showCreateModal]);

    // Fetch fixed assets when companyId is available
    useEffect(() => {
        if (companyId) {
            fetchFixedAssets();
        }
    }, [companyId]);

    // Calculate capitalized cost and net book value
    useEffect(() => {
        if (createFormData.purchase_cost) {
            const purchaseCost = parseFloat(createFormData.purchase_cost) || 0;
            const additionalCost = parseFloat(createFormData.additional_cost) || 0;
            const capitalizedCost = purchaseCost + additionalCost;

            setCreateFormData(prev => ({
                ...prev,
                capitalized_cost: capitalizedCost.toString(),
                net_book_value: (capitalizedCost - (parseFloat(prev.accumulated_depreciation) || 0)).toString()
            }));
        }
    }, [createFormData.purchase_cost, createFormData.additional_cost, createFormData.accumulated_depreciation]);

    // Auto-fill from category when category is selected
    useEffect(() => {
        if (createFormData.category_id && assetCategories.length > 0) {
            const selectedCategory = assetCategories.find(cat => String(cat.id) === String(createFormData.category_id));
            if (selectedCategory) {
                // Auto-fill useful life years from category (read-only)
                if (selectedCategory.useful_life_years) {
                    setCreateFormData(prev => ({
                        ...prev,
                        useful_life_years: selectedCategory.useful_life_years
                    }));
                }

                // Auto-fill depreciation rate from category (read-only)
                if (selectedCategory.depreciation_rate) {
                    setCreateFormData(prev => ({
                        ...prev,
                        depreciation_rate: selectedCategory.depreciation_rate
                    }));
                }

                // Auto-fill depreciation method from category (read-only)
                if (selectedCategory.depreciation_method_id) {
                    setCreateFormData(prev => ({
                        ...prev,
                        depreciation_method: String(selectedCategory.depreciation_method_id)
                    }));

                    // Update the search term display for depreciation method
                    const method = depreciationMethods.find(m => String(m.id) === String(selectedCategory.depreciation_method_id));
                    if (method) {
                        setSearchTerms(prev => ({
                            ...prev,
                            depreciation_method: method.name || ""
                        }));
                    }
                }
            }
        }
    }, [createFormData.category_id, assetCategories, depreciationMethods]);

    const fetchFixedAssets = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = getValidCompanyId();

            if (!token || !validCompanyId) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Authentication failed. Please log in again.'
                });
                setLoading(false);
                return;
            }

            const response = await axios.get(`${BASE_URL}/v1/fixedassets/fetch`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: validCompanyId }
            });

            if (response.data.status && Array.isArray(response.data.data)) {
                setFixedAssets(response.data.data);
                const totalValue = response.data.data.reduce((sum, asset) => sum + (parseFloat(asset.purchase_cost) || 0), 0);
                const totalDepreciation = response.data.data.reduce((sum, asset) => sum + (parseFloat(asset.accumulated_depreciation) || 0), 0);
                const netBookValue = response.data.data.reduce((sum, asset) => sum + (parseFloat(asset.net_book_value) || 0), 0);

                setSelectedMetrics({
                    total_assets: response.data.data.length,
                    total_value: totalValue,
                    total_depreciation: totalDepreciation,
                    net_book_value: netBookValue
                });
            } else {
                setFixedAssets([]);
            }
        } catch (error) {
            console.error("Error fetching fixed assets:", error);
            let errorMessage = "Failed to fetch fixed assets";
            if (error.response?.data?.message) {
                errorMessage = typeof error.response.data.message === 'object'
                    ? Object.values(error.response.data.message).flat().join(', ')
                    : error.response.data.message;
            }
            Swal.fire({ icon: 'error', title: 'Error', text: errorMessage });
            setFixedAssets([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssetCategories = async () => {
        setCreateLoading(prev => ({ ...prev, categories: true }));
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = getValidCompanyId();
            if (!validCompanyId) return;

            const response = await axios.get(`${BASE_URL}/v1/assetcategories`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: validCompanyId }
            });
            if (response.data.status && Array.isArray(response.data.data)) {
                setAssetCategories(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching asset categories:", error);
        } finally {
            setCreateLoading(prev => ({ ...prev, categories: false }));
        }
    };

    const fetchSuppliers = async () => {
        setCreateLoading(prev => ({ ...prev, suppliers: true }));
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = getValidCompanyId();

            const response = await axios.get(`${BASE_URL}/v1/beneficiary/get`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: validCompanyId }
            });

            if (response.data.status && Array.isArray(response.data.data)) {
                setSuppliers(response.data.data);
            } else if (Array.isArray(response.data)) {
                setSuppliers(response.data);
            } else {
                setSuppliers([]);
            }
        } catch (error) {
            console.error("Error fetching suppliers:", error);
        } finally {
            setCreateLoading(prev => ({ ...prev, suppliers: false }));
        }
    };

    const fetchDepreciationMethods = async () => {
        setCreateLoading(prev => ({ ...prev, methods: true }));
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`${BASE_URL}/v1/fixedassets/get-depreciation-method`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (Array.isArray(response.data?.data)) {
                setDepreciationMethods(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching depreciation methods:", error);
        } finally {
            setCreateLoading(prev => ({ ...prev, methods: false }));
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
        '/fixed-assets': 'Fixed Assets',
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const maxSize = 2 * 1024 * 1024;
        const validFiles = files.filter(file => {
            if (file.size > maxSize) {
                Swal.fire('Warning', `${file.name} exceeds 2MB and will not be uploaded`, 'warning');
                return false;
            }
            return true;
        });
        setUploadedFiles(prev => [...prev, ...validFiles]);
        setCreateFormData(prev => ({
            ...prev,
            asset_document: [...(prev.asset_document || []), ...validFiles]
        }));
    };

    const removeFile = (index) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
        setCreateFormData(prev => ({
            ...prev,
            asset_document: prev.asset_document.filter((_, i) => i !== index)
        }));
    };

    const handleViewClick = async (asset) => {
        setModalMode("view");
        setSelectedAsset(asset);
        setModalLoading(true);
        setShowViewEditModal(true);
        setModalData({
            id: asset.id,
            asset_name: asset.asset_name || "",
            asset_code: asset.asset_code || "",
            description: asset.description || "",
            serial_number: asset.serial_number || "",
            category_id: asset.category_id || "",
            category_name: asset.category?.name || asset.asset_category?.name || "N/A",
            purchase_date: asset.purchase_date || "",
            supplier_id: asset.supplier_id || "",
            supplier_name: asset.supplier?.name || "N/A",
            invoice_number: asset.invoice_number || "",
            purchase_cost: asset.purchase_cost || "",
            additional_cost: asset.additional_cost || "0",
            capitalized_cost: asset.capitalized_cost || "",
            residual_value: asset.residual_value || "0",
            useful_life_years: asset.useful_life_years || "",
            depreciation_method_id: asset.depreciation_method_id || "",
            depreciation_method_name: asset.depre_method?.name || "N/A",
            depreciation_rate: asset.depreciation_rate || "",
            accumulated_depreciation: asset.accumulated_depreciation || "0",
            net_book_value: asset.net_book_value || "",
            documents: asset.documents || []
        });
        setModalLoading(false);
    };

    const handleEditClick = async (asset) => {
        setModalMode("edit");
        setSelectedAsset(asset);
        setModalLoading(true);
        setShowViewEditModal(true);

        console.log("Asset data for edit:", asset); // Debug log

        setModalData({
            id: asset.id,
            asset_name: asset.asset_name || "",
            asset_code: asset.asset_code || "",
            description: asset.description || "",
            serial_number: asset.serial_number || "",
            category_id: asset.category_id || "",
            category_name: asset.asset_category?.name || asset.category?.name || "N/A",
            purchase_date: asset.purchase_date || "",
            supplier_id: asset.supplier_id || "",
            supplier_name: asset.supplier?.name || "N/A",
            invoice_number: asset.invoice_number || "",
            purchase_cost: asset.purchase_cost || "",
            additional_cost: asset.additional_cost || "0",
            capitalized_cost: asset.capitalized_cost || "",
            residual_value: asset.residual_value || "0",
            useful_life_years: asset.useful_life_years || "", // Make sure this is captured
            depreciation_method_id: asset.depreciation_method_id || "",
            depreciation_method_name: asset.depre_method?.name || "N/A",
            depreciation_rate: asset.depreciation_rate || "",
            accumulated_depreciation: asset.accumulated_depreciation || "0",
            net_book_value: asset.net_book_value || "",
            documents: asset.documents || []
        });

        setModalLoading(false);
    };

    // Handle modal input changes for edit
    const handleModalChange = (field, value) => {
        setModalData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Search handlers
    const handleSearchChange = (field, e) => {
        const value = e.target.value;
        setSearchTerms(prev => ({ ...prev, [field]: value }));
        if (!showDropdowns[field]) {
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
        }
        setDropdownTrigger(prev => ({ ...prev, [field]: true }));
    };

    const handleInputClick = (field) => {
        if (!showDropdowns[field]) {
            const currentSearchTerm = searchTerms[field];
            if (currentSearchTerm && !dropdownTrigger[field]) {
                setSearchTerms(prev => ({ ...prev, [field]: "" }));
            }
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
            setDropdownTrigger(prev => ({ ...prev, [field]: false }));
        } else {
            setDropdownTrigger(prev => ({ ...prev, [field]: false }));
        }
    };

    const handleCategorySelect = (item) => {
        setCreateFormData(prev => ({
            ...prev,
            category_id: item.id,
            useful_life_years: item.useful_life_years || "",
            depreciation_rate: item.depreciation_rate || "",
            depreciation_method: item.depreciation_method_id ? String(item.depreciation_method_id) : "",
        }));

        setSearchTerms(prev => ({ ...prev, category: item.name || "" }));
        setShowDropdowns(prev => ({ ...prev, category: false }));
        setDropdownTrigger(prev => ({ ...prev, category: false }));
    };

    const handleSupplierSelect = (item) => {
        setCreateFormData(prev => ({
            ...prev,
            supplier_id: item.id
        }));
        setSearchTerms(prev => ({ ...prev, supplier: item.name || item.supplier_name || "" }));
        setShowDropdowns(prev => ({ ...prev, supplier: false }));
        setDropdownTrigger(prev => ({ ...prev, supplier: false }));
    };

    const handleDepreciationMethodSelect = (item) => {
        setCreateFormData(prev => ({
            ...prev,
            depreciation_method: String(item.id)
        }));
        setSearchTerms(prev => ({ ...prev, depreciation_method: item.name || "" }));
        setShowDropdowns(prev => ({ ...prev, depreciation_method: false }));
        setDropdownTrigger(prev => ({ ...prev, depreciation_method: false }));
    };

    // Modal dropdown handlers
    const handleModalSearchChange = (field, e) => {
        const value = e.target.value;
        setSearchTerms(prev => ({ ...prev, [field]: value }));
        if (!showDropdowns[field]) {
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
        }
        setDropdownTrigger(prev => ({ ...prev, [field]: true }));
    };

    const handleModalInputClick = (field) => {
        if (!showDropdowns[field]) {
            const currentSearchTerm = searchTerms[field];
            if (currentSearchTerm && !dropdownTrigger[field]) {
                setSearchTerms(prev => ({ ...prev, [field]: "" }));
            }
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
            setDropdownTrigger(prev => ({ ...prev, [field]: false }));
        } else {
            setDropdownTrigger(prev => ({ ...prev, [field]: false }));
        }
    };

    const handleModalCategorySelect = (item) => {
        setModalData(prev => ({
            ...prev,
            category_id: item.id,
            category_name: item.name,
            useful_life_years: item.useful_life_years || "",
            depreciation_rate: item.depreciation_rate || "",
            depreciation_method_id: item.depreciation_method_id ? String(item.depreciation_method_id) : "",
        }));

        if (item.depreciation_method_id && depreciationMethods.length > 0) {
            const method = depreciationMethods.find(m => String(m.id) === String(item.depreciation_method_id));
            if (method) {
                setModalData(prev => ({
                    ...prev,
                    depreciation_method_name: method.name || ""
                }));
            }
        }

        setSearchTerms(prev => ({ ...prev, category: item.name || "" }));
        setShowDropdowns(prev => ({ ...prev, category: false }));
        setDropdownTrigger(prev => ({ ...prev, category: false }));
    };

    const handleModalSupplierSelect = (item) => {
        setModalData(prev => ({
            ...prev,
            supplier_id: item.id,
            supplier_name: item.name || item.supplier_name || ""
        }));
        setSearchTerms(prev => ({ ...prev, supplier: item.name || item.supplier_name || "" }));
        setShowDropdowns(prev => ({ ...prev, supplier: false }));
        setDropdownTrigger(prev => ({ ...prev, supplier: false }));
    };

    const handleModalDepreciationMethodSelect = (item) => {
        setModalData(prev => ({
            ...prev,
            depreciation_method_id: String(item.id),
            depreciation_method_name: item.name
        }));
        setSearchTerms(prev => ({ ...prev, depreciation_method: item.name || "" }));
        setShowDropdowns(prev => ({ ...prev, depreciation_method: false }));
        setDropdownTrigger(prev => ({ ...prev, depreciation_method: false }));
    };

    const getSelectedName = (field) => {
        const idField = field === 'category' ? 'category_id' :
            field === 'supplier' ? 'supplier_id' : 'depreciation_method';
        const id = createFormData[idField];
        if (!id) return "";

        let items = [];
        if (field === 'category') items = assetCategories;
        else if (field === 'supplier') items = suppliers;
        else items = depreciationMethods;

        const item = items.find(i => String(i.id) === String(id));
        if (!item) return "";
        if (field === 'category') return item.name || "";
        if (field === 'supplier') return item.name || item.supplier_name || "";
        return item.name || "";
    };

    const getModalSelectedName = (field) => {
        const idField = field === 'category' ? 'category_id' :
            field === 'supplier' ? 'supplier_id' : 'depreciation_method_id';
        const id = modalData[idField];
        if (!id) return "";

        let items = [];
        if (field === 'category') items = assetCategories;
        else if (field === 'supplier') items = suppliers;
        else items = depreciationMethods;

        const item = items.find(i => String(i.id) === String(id));
        if (!item) return "";
        if (field === 'category') return item.name || "";
        if (field === 'supplier') return item.name || item.supplier_name || "";
        return item.name || "";
    };

    const filterItems = (field) => {
        const searchTerm = (searchTerms[field] || "").toLowerCase();
        let items = [];

        if (field === 'category') items = assetCategories;
        else if (field === 'supplier') items = suppliers;
        else items = depreciationMethods;

        if (!Array.isArray(items)) return [];

        if (!searchTerm) return items;

        return items.filter(item => {
            if (!item) return false;
            let displayName = '';
            if (field === 'category') displayName = item.name || "";
            else if (field === 'supplier') displayName = item.name || item.supplier_name || "";
            else displayName = item.name || "";
            return displayName.toLowerCase().includes(searchTerm);
        });
    };

    const handleEditSave = async () => {
        setSaveLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = getValidCompanyId();

            if (!validCompanyId) {
                Swal.fire({
                    icon: 'error',
                    title: 'Company ID Error',
                    text: 'Company ID not found. Please log in again.'
                });
                setSaveLoading(false);
                return;
            }

            // Make sure all required fields are present
            const payload = {
                id: modalData.id, // IMPORTANT: Include id in payload
                asset_name: modalData.asset_name,
                asset_code: modalData.asset_code,
                description: modalData.description || '',
                serial_number: modalData.serial_number || '',
                category_id: modalData.category_id,
                purchase_date: modalData.purchase_date,
                supplier_id: modalData.supplier_id || '',
                invoice_number: modalData.invoice_number || '',
                purchase_cost: modalData.purchase_cost,
                additional_cost: modalData.additional_cost || '0',
                residual_value: modalData.residual_value || '0',
                useful_life_years: modalData.useful_life_years || '', // Make sure this is included
                depreciation_method: modalData.depreciation_method_id || null,
                depreciation_rate: modalData.depreciation_rate || '',
                accumulated_depreciation: modalData.accumulated_depreciation || '0',
                company_id: validCompanyId
            };

            // Validate required fields
            const requiredFields = ['id', 'asset_name', 'asset_code', 'category_id', 'purchase_date', 'purchase_cost', 'useful_life_years', 'company_id'];
            for (const field of requiredFields) {
                if (!payload[field]) {
                    console.error(`Missing required field: ${field}`);
                    Swal.fire({
                        icon: 'error',
                        title: 'Validation Error',
                        text: `${field.replace(/_/g, ' ')} is required`
                    });
                    setSaveLoading(false);
                    return;
                }
            }

            console.log("Updating fixed asset with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/v1/fixedassets/edit`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log("Edit response:", response.data);

            if (response.data.status) {
                await fetchFixedAssets(); // Refresh the list
                setShowViewEditModal(false); // Close modal
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: response.data.message || 'Fixed asset updated successfully!',
                    timer: 3000,
                    showConfirmButton: false
                });
            } else {
                let errorMessage = "Failed to update fixed asset";
                if (response.data.message) {
                    if (typeof response.data.message === 'object') {
                        // Format validation errors
                        errorMessage = Object.values(response.data.message)
                            .flat()
                            .join('\n');
                    } else {
                        errorMessage = response.data.message;
                    }
                }
                Swal.fire({ icon: 'error', title: 'Error!', text: errorMessage });
            }
        } catch (error) {
            console.error("Error updating fixed asset:", error);
            let errorMessage = "Failed to update fixed asset";
            if (error.response?.data?.message) {
                if (typeof error.response.data.message === 'object') {
                    errorMessage = Object.values(error.response.data.message)
                        .flat()
                        .join('\n');
                } else {
                    errorMessage = error.response.data.message;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            Swal.fire({ icon: 'error', title: 'Error!', text: errorMessage });
        } finally {
            setSaveLoading(false);
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        const requiredFields = ['asset_name', 'asset_code', 'category_id', 'purchase_date', 'purchase_cost'];
        for (const field of requiredFields) {
            if (!createFormData[field]) {
                Swal.fire("Error", `Please fill in all required fields`, "error");
                return;
            }
        }

        const validCompanyId = getValidCompanyId();
        if (!validCompanyId) {
            Swal.fire({
                icon: 'error',
                title: 'Company ID Error',
                text: 'Company ID not found. Please log in again.',
                confirmButtonText: 'Go to Login'
            }).then(() => navigate('/login'));
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");

            const formData = new FormData();
            const formFields = {
                asset_name: createFormData.asset_name,
                asset_code: createFormData.asset_code,
                description: createFormData.description || '',
                serial_number: createFormData.serial_number || '',
                category_id: createFormData.category_id,
                purchase_date: createFormData.purchase_date,
                supplier_id: createFormData.supplier_id || '',
                invoice_number: createFormData.invoice_number || '',
                purchase_cost: createFormData.purchase_cost,
                additional_cost: createFormData.additional_cost || '0',
                residual_value: createFormData.residual_value || '0',
                useful_life_years: createFormData.useful_life_years,
                depreciation_method: createFormData.depreciation_method || '',
                depreciation_rate: createFormData.depreciation_rate || '',
                accumulated_depreciation: createFormData.accumulated_depreciation || '0',
                company_id: validCompanyId
            };

            Object.keys(formFields).forEach(key => {
                formData.append(key, formFields[key]);
            });

            if (createFormData.asset_document && createFormData.asset_document.length > 0) {
                createFormData.asset_document.forEach(file => {
                    formData.append('asset_document[]', file);
                });
            }

            const response = await axios.post(`${BASE_URL}/v1/fixedassets/create`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data?.status) {
                Swal.fire({
                    title: "Success",
                    text: response.data.message || "Fixed asset created successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });
                resetCreateForm();
                onClose();
                await fetchFixedAssets();
            } else {
                Swal.fire("Error", response.data?.message || "Failed to create fixed asset", "error");
            }
        } catch (error) {
            console.error("Error creating fixed asset:", error);
            let errorMessage = "Something went wrong while creating the asset";
            if (error.response?.data?.message) {
                errorMessage = typeof error.response.data.message === 'object'
                    ? Object.values(error.response.data.message).flat().join(', ')
                    : error.response.data.message;
            }
            Swal.fire("Error", errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = async (asset) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                const validCompanyId = getValidCompanyId();
                if (!validCompanyId) {
                    Swal.fire({ icon: 'error', title: 'Error', text: 'Company ID not found.' });
                    return;
                }

                const response = await axios.delete(`${BASE_URL}/v1/fixedassets/${asset.id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { company_id: validCompanyId }
                });

                if (response.data.status) {
                    await fetchFixedAssets();
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message || 'Fixed asset deleted successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || "Failed to delete fixed asset");
                }
            } catch (error) {
                console.error("Error deleting fixed asset:", error);
                Swal.fire({ icon: 'error', title: 'Error!', text: error.response?.data?.message || "Failed to delete fixed asset" });
            }
        }
    };

    const resetCreateForm = () => {
        const validCompanyId = getValidCompanyId();
        setCreateFormData({
            asset_name: "",
            asset_code: "",
            description: "",
            serial_number: "",
            category_id: "",
            purchase_date: "",
            supplier_id: "",
            invoice_number: "",
            purchase_cost: "",
            additional_cost: "0",
            capitalized_cost: "",
            residual_value: "0",
            useful_life_years: "",
            depreciation_method: "",
            depreciation_rate: "",
            accumulated_depreciation: "0",
            net_book_value: "",
            asset_document: [],
            company_id: validCompanyId || ""
        });
        setUploadedFiles([]);
        setSearchTerms({ category: "", supplier: "", depreciation_method: "" });
        setDropdownTrigger({ category: false, supplier: false, depreciation_method: false });
    };

    const onClose = () => {
        setShowCreateModal(false);
        resetCreateForm();
        setShowDropdowns({ category: false, supplier: false, depreciation_method: false });
    };

    const formatCurrency = (value) => {
        if (!value) return "0.00";
        return parseFloat(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const TableSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname] || "Fixed Assets"} />

            <div className={classes.Container}>
                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={`${classes.btns} ${isActive ? classes.active : ""}`} onClick={() => setIsActive(true)}>
                            Assets
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/categories')}>
                            Categories
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/locations')}>
                            Locations
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/assignments')}>
                            Assignments
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/depreciation')}>
                            Depreciation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/revaluation')}>
                            Revaluation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/disposal')}>
                            Disposal
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/maintenance')}>
                            Maintenance
                        </button>
                    </div>

                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Fixed Assets</h2>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Assets</p>
                                    <p className={classes.kdptg1}>{selectedMetrics?.total_assets?.toLocaleString() ?? '0'}</p>
                                    <div className={classes.rvnue}><p className={classes.skaltxt}>Active assets</p></div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Value</p>
                                    <p className={classes.kdptg2}>{formatCurrency(selectedMetrics?.total_value)}</p>
                                    <div className={classes.rvnue}><p className={classes.skaltxt}>Purchase cost</p></div>
                                </div>
                            </div>
                            <div className={classes.fiftkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Depreciation</p>
                                    <p className={classes.kdptg3}>{formatCurrency(selectedMetrics?.total_depreciation)}</p>
                                    <div className={classes.rvnue}><p className={classes.skaltxt}>Accumulated</p></div>
                                </div>
                            </div>
                            <div className={classes.frstkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Net Book Value</p>
                                    <p className={classes.kdptg4}>{formatCurrency(selectedMetrics?.net_book_value)}</p>
                                    <div className={classes.rvnue}><p className={classes.skaltxt}>Current value</p></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input type="text" className={classes.searchBar} placeholder="Search assets" />
                            </div>
                            <div className={classes.selectWrapper}>
                                <select className={classes.dropdown}>
                                    <option value="">All Categories</option>
                                    {assetCategories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                                <span className={classes.dropdownArrow}>▼</span>
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>
                        <button className={classes.btnss} onClick={() => setShowCreateModal(true)}>+ Add Fixed Asset</button>
                    </div>

                    <table className={classes.invoiceTable}>
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Asset Code</th>
                                <th>Asset Name</th>
                                <th>Category</th>
                                <th>Purchase Date</th>
                                <th style={{ textAlign: 'right' }}>Purchase Cost</th>
                                <th style={{ textAlign: 'right' }}>Net Book Value</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton />
                            ) : fixedAssets.length > 0 ? (
                                fixedAssets.map((asset, index) => (
                                    <tr key={asset.id}>
                                        <td>{index + 1}</td>
                                        <td>{asset.asset_code || "—"}</td>
                                        <td>{asset.asset_name || "—"}</td>
                                        <td>{asset.asset_category?.name || asset.category?.name || "—"}</td>
                                        <td>{asset.purchase_date || "—"}</td>
                                        <td style={{ textAlign: 'right' }}>{formatCurrency(asset.purchase_cost)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatCurrency(asset.net_book_value)}</td>
                                        <td>
                                            <span className={asset.net_book_value > 0 ? classes.activeStatus : classes.fullyDepreciated}>
                                                {asset.net_book_value > 0 ? "Active" : "Fully Depreciated"}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                <Eye size={16} style={{ cursor: "pointer", color: "#007bff" }} onClick={() => handleViewClick(asset)} />
                                                <Pencil size={16} style={{ cursor: "pointer", color: "#28a745" }} onClick={() => handleEditClick(asset)} />
                                                <Trash2 size={16} style={{ cursor: "pointer", color: "#dc3545" }} onClick={() => handleDeleteClick(asset)} />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>No fixed assets found</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View/Edit Modal */}
            {showViewEditModal && (
                <div className={classes.overlay}>
                    <div className={classes.modalLarge}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <FileText className={classes.icon} />
                                <h2>{modalMode === "edit" ? "Edit Fixed Asset" : "View Fixed Asset"}</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={() => setShowViewEditModal(false)} disabled={saveLoading}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={classes.formGrid2}>
                            {modalLoading ? (
                                <div className={classes.loadingContainer}>Loading asset details...</div>
                            ) : (
                                <>
                                    <div className={classes.formGroup}>
                                        <label>Asset Code</label>
                                        <input type="text" value={modalData.asset_code} readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("asset_code", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2} />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Asset Name</label>
                                        <input type="text" value={modalData.asset_name} readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("asset_name", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2} />
                                    </div>

                                    <div className={classes.formGroupFull}>
                                        <label>Description</label>
                                        <textarea value={modalData.description} readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("description", e.target.value)}
                                            className={modalMode === "edit" ? classes.textarea2 : classes.textarea2} rows="3" />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Serial Number</label>
                                        <input type="text" value={modalData.serial_number} readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("serial_number", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2} />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Category</label>
                                        {modalMode === "edit" ? (
                                            <div className={classes.searchSelectWrapper} ref={categoryRef}>
                                                <div className={classes.searchInputContainer}>
                                                    <input type="text" className={classes.searchSelectInput} placeholder="Search and select category..."
                                                        value={showDropdowns.category ? searchTerms.category : getModalSelectedName('category')}
                                                        onChange={(e) => handleModalSearchChange('category', e)}
                                                        onClick={() => handleModalInputClick('category')}
                                                        onFocus={() => handleModalInputClick('category')}
                                                        required />
                                                </div>
                                                {showDropdowns.category && (
                                                    <div className={classes.searchDropdown}>
                                                        <div className={classes.dropdownList}>
                                                            {createLoading.categories ? <div className={classes.dropdownItem}>Loading categories...</div>
                                                                : filterItems('category').length === 0 ? <div className={classes.dropdownItem}>No categories found</div>
                                                                    : filterItems('category').map((item) => (
                                                                        <div key={item.id} className={`${classes.dropdownItem} ${modalData.category_id === item.id ? classes.selectedItem : ''}`}
                                                                            onClick={() => handleModalCategorySelect(item)}>
                                                                            {item.name}
                                                                        </div>
                                                                    ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <input type="text" value={modalData.category_name} readOnly className={classes.Field2} />
                                        )}
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Purchase Date</label>
                                        <input type={modalMode === "edit" ? "date" : "text"} value={modalData.purchase_date}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("purchase_date", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2} />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Supplier</label>
                                        {modalMode === "edit" ? (
                                            <div className={classes.searchSelectWrapper} ref={supplierRef}>
                                                <div className={classes.searchInputContainer}>
                                                    <input type="text" className={classes.searchSelectInput} placeholder="Search and select supplier..."
                                                        value={showDropdowns.supplier ? searchTerms.supplier : getModalSelectedName('supplier')}
                                                        onChange={(e) => handleModalSearchChange('supplier', e)}
                                                        onClick={() => handleModalInputClick('supplier')}
                                                        onFocus={() => handleModalInputClick('supplier')} />
                                                </div>
                                                {showDropdowns.supplier && (
                                                    <div className={classes.searchDropdown}>
                                                        <div className={classes.dropdownList}>
                                                            {createLoading.suppliers ? <div className={classes.dropdownItem}>Loading suppliers...</div>
                                                                : filterItems('supplier').length === 0 ? <div className={classes.dropdownItem}>No suppliers found</div>
                                                                    : filterItems('supplier').map((item) => (
                                                                        <div key={item.id} className={`${classes.dropdownItem} ${modalData.supplier_id === item.id ? classes.selectedItem : ''}`}
                                                                            onClick={() => handleModalSupplierSelect(item)}>
                                                                            {item.name || item.supplier_name}
                                                                        </div>
                                                                    ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <input type="text" value={modalData.supplier_name} readOnly className={classes.Field2} />
                                        )}
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Invoice Number</label>
                                        <input type="text" value={modalData.invoice_number} readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("invoice_number", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2} />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Purchase Cost</label>
                                        <input type="number" value={modalData.purchase_cost} readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("purchase_cost", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2} step="0.01" style={{ textAlign: 'right' }} />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Additional Cost</label>
                                        <input type="number" value={modalData.additional_cost} readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("additional_cost", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2} step="0.01" style={{ textAlign: 'right' }} />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Residual Value</label>
                                        <input type="number" value={modalData.residual_value} readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("residual_value", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2} step="0.01" style={{ textAlign: 'right' }} />
                                    </div>

                                    {/* READ-ONLY FIELDS - Auto-filled from category */}
                                    <div className={classes.formGroup}>
                                        <label>Useful Life (Years)</label>
                                        <input type="number" value={modalData.useful_life_years} readOnly className={classes.readonlyField} min="1" placeholder="Auto-filled from category" />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Depreciation Method</label>
                                        <input type="text" value={modalData.depreciation_method_name} readOnly className={classes.readonlyField} placeholder="Auto-filled from category" />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Depreciation Rate (%)</label>
                                        <input type="number" value={modalData.depreciation_rate} readOnly className={classes.readonlyField} step="0.01" style={{ textAlign: 'right' }} placeholder="Auto-filled from category" />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Accumulated Depreciation</label>
                                        <input type="number" value={modalData.accumulated_depreciation} readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("accumulated_depreciation", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2} step="0.01" style={{ textAlign: 'right' }} />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Net Book Value</label>
                                        <input type="number" value={modalData.net_book_value} readOnly className={classes.readonlyField} step="0.01" style={{ textAlign: 'right' }} />
                                    </div>
                                </>
                            )}
                        </div>

                        <button className={classes.saveBtn} onClick={modalMode === "edit" ? handleEditSave : () => setShowViewEditModal(false)} disabled={saveLoading}>
                            {saveLoading ? (<span className={classes.loadingText}><span className={classes.spinner}></span>Saving...</span>) : (modalMode === "edit" ? "Save Changes" : "Close")}
                        </button>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className={classes.overlay}>
                    <div className={classes.modalLarge}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <Package className={classes.icon} />
                                <h2>Add Fixed Asset</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={onClose}><X size={18} /></button>
                        </div>

                        <form onSubmit={handleCreateSubmit}>
                            <div className={classes.formGrid2}>
                                <div className={classes.formGroup}>
                                    <label>Asset Name *</label>
                                    <input type="text" name="asset_name" placeholder="e.g., Delivery Truck" value={createFormData.asset_name} onChange={handleCreateChange} required />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Asset Code *</label>
                                    <input type="text" name="asset_code" placeholder="e.g., AST-001" value={createFormData.asset_code} onChange={handleCreateChange} required />
                                </div>

                                <div className={classes.formGroupFull}>
                                    <label>Description</label>
                                    <textarea name="description" placeholder="Enter asset description..." value={createFormData.description} onChange={handleCreateChange} rows="3" />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Serial Number</label>
                                    <input type="text" name="serial_number" placeholder="Serial number" value={createFormData.serial_number} onChange={handleCreateChange} />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Category *</label>
                                    <div className={classes.searchSelectWrapper} ref={categoryRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input type="text" className={classes.searchSelectInput} placeholder="Search and select category..."
                                                value={showDropdowns.category ? searchTerms.category : getSelectedName('category')}
                                                onChange={(e) => handleSearchChange('category', e)}
                                                onClick={() => handleInputClick('category')}
                                                onFocus={() => handleInputClick('category')}
                                                required />
                                        </div>
                                        {showDropdowns.category && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.categories ? <div className={classes.dropdownItem}>Loading categories...</div>
                                                        : filterItems('category').length === 0 ? <div className={classes.dropdownItem}>No categories found</div>
                                                            : filterItems('category').map((item) => (
                                                                <div key={item.id} className={`${classes.dropdownItem} ${createFormData.category_id === item.id ? classes.selectedItem : ''}`}
                                                                    onClick={() => handleCategorySelect(item)}>
                                                                    {item.name}
                                                                </div>
                                                            ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Purchase Date *</label>
                                    <input type="date" name="purchase_date" value={createFormData.purchase_date} onChange={handleCreateChange} required />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Supplier</label>
                                    <div className={classes.searchSelectWrapper} ref={supplierRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input type="text" className={classes.searchSelectInput} placeholder="Search and select supplier..."
                                                value={showDropdowns.supplier ? searchTerms.supplier : getSelectedName('supplier')}
                                                onChange={(e) => handleSearchChange('supplier', e)}
                                                onClick={() => handleInputClick('supplier')}
                                                onFocus={() => handleInputClick('supplier')} />
                                        </div>
                                        {showDropdowns.supplier && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.suppliers ? <div className={classes.dropdownItem}>Loading suppliers...</div>
                                                        : filterItems('supplier').length === 0 ? <div className={classes.dropdownItem}>No suppliers found</div>
                                                            : filterItems('supplier').map((item) => (
                                                                <div key={item.id} className={`${classes.dropdownItem} ${createFormData.supplier_id === item.id ? classes.selectedItem : ''}`}
                                                                    onClick={() => handleSupplierSelect(item)}>
                                                                    {item.name || item.supplier_name}
                                                                </div>
                                                            ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Invoice Number</label>
                                    <input type="text" name="invoice_number" placeholder="Invoice number" value={createFormData.invoice_number} onChange={handleCreateChange} />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Purchase Cost *</label>
                                    <input type="number" name="purchase_cost" placeholder="0.00" value={createFormData.purchase_cost} onChange={handleCreateChange} min="0" step="0.01" required style={{ textAlign: 'right' }} />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Additional Cost</label>
                                    <input type="number" name="additional_cost" placeholder="0.00" value={createFormData.additional_cost} onChange={handleCreateChange} min="0" step="0.01" style={{ textAlign: 'right' }} />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Capitalized Cost</label>
                                    <input type="number" name="capitalized_cost" value={createFormData.capitalized_cost} readOnly className={classes.readonlyField} style={{ textAlign: 'right' }} />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Residual Value</label>
                                    <input type="number" name="residual_value" placeholder="0.00" value={createFormData.residual_value} onChange={handleCreateChange} min="0" step="0.01" style={{ textAlign: 'right' }} />
                                </div>

                                {/* READ-ONLY FIELDS - Auto-filled from category */}
                                <div className={classes.formGroup}>
                                    <label>Useful Life (Years)</label>
                                    <input type="number" name="useful_life_years" value={createFormData.useful_life_years} readOnly className={classes.readonlyField} placeholder="Auto-filled from category" />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Depreciation Method</label>
                                    <input type="text" value={getSelectedName('depreciation_method')} readOnly className={classes.readonlyField} placeholder="Auto-filled from category" />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Depreciation Rate (%)</label>
                                    <input type="number" name="depreciation_rate" value={createFormData.depreciation_rate} readOnly className={classes.readonlyField} step="0.01" style={{ textAlign: 'right' }} placeholder="Auto-filled from category" />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Accumulated Depreciation</label>
                                    <input type="number" name="accumulated_depreciation" placeholder="0.00" value={createFormData.accumulated_depreciation} onChange={handleCreateChange} min="0" step="0.01" style={{ textAlign: 'right' }} />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Net Book Value</label>
                                    <input type="number" name="net_book_value" value={createFormData.net_book_value} readOnly className={classes.readonlyField} style={{ textAlign: 'right' }} />
                                </div>

                                <div className={classes.formGroupFull}>
                                    <label>Asset Documents</label>
                                    <div className={classes.fileUploadArea}>
                                        <input type="file" ref={fileInputRef} onChange={handleFileChange} multiple accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} />
                                        <button type="button" className={classes.uploadBtn} onClick={() => fileInputRef.current?.click()}>
                                            <Upload size={16} /> Upload Documents
                                        </button>
                                        <span className={classes.fileHint}>(PDF, JPG, JPEG, PNG - Max 2MB each)</span>
                                    </div>
                                    {uploadedFiles.length > 0 && (
                                        <div className={classes.fileList}>
                                            {uploadedFiles.map((file, index) => (
                                                <div key={index} className={classes.fileItem}>
                                                    <File size={16} />
                                                    <span className={classes.fileName}>{file.name}</span>
                                                    <span className={classes.fileSize}>({(file.size / 1024).toFixed(2)} KB)</span>
                                                    <button type="button" className={classes.removeFileBtn} onClick={() => removeFile(index)}><X size={14} /></button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button type="submit" className={classes.saveBtn} disabled={submitting}>
                                {submitting ? "Creating..." : "Add Fixed Asset"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default FixedAsset;